/**
 * Admin Audit Logging
 *
 * All admin actions must be logged for compliance and security.
 * Logs are stored in the audit_logs Firestore collection.
 */

import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { AdminRole, AdminActionType, AuditLogEntry } from '@/integrations/firebase/types';

interface LogActionParams {
  actorUserId: string;
  actorEmail: string;
  actorRole: AdminRole;
  actionType: AdminActionType;
  targetOrgId?: string;
  targetUserId?: string;
  targetJobId?: string;
  targetRunId?: string;
  description: string;
  diff?: {
    before: Record<string, any>;
    after: Record<string, any>;
  };
}

/**
 * Log an admin action to the audit log
 */
export async function logAdminAction(params: LogActionParams): Promise<string> {
  const {
    actorUserId,
    actorEmail,
    actorRole,
    actionType,
    targetOrgId,
    targetUserId,
    targetJobId,
    targetRunId,
    description,
    diff,
  } = params;

  // Get IP and user agent if available (client-side)
  let ip: string | undefined;
  let userAgent: string | undefined;

  if (typeof window !== 'undefined') {
    userAgent = navigator.userAgent;
    // IP will be captured server-side if needed
  }

  const logEntry: Omit<AuditLogEntry, 'id' | 'timestamp'> & { timestamp: any } = {
    actorUserId,
    actorEmail,
    actorRole,
    actionType,
    description,
    timestamp: serverTimestamp(),
    ...(targetOrgId && { targetOrgId }),
    ...(targetUserId && { targetUserId }),
    ...(targetJobId && { targetJobId }),
    ...(targetRunId && { targetRunId }),
    ...(diff && { diff }),
    ...(ip && { ip }),
    ...(userAgent && { userAgent }),
  };

  try {
    const docRef = await addDoc(collection(db, 'audit_logs'), logEntry);
    return docRef.id;
  } catch (error) {
    console.error('Failed to write audit log:', error);
    // Don't throw - audit log failure shouldn't block the action
    // But log to console for debugging
    return '';
  }
}

/**
 * Helper to create a diff object for logging changes
 */
export function createDiff<T extends Record<string, any>>(
  before: T,
  after: Partial<T>
): { before: Record<string, any>; after: Record<string, any> } | undefined {
  const beforeDiff: Record<string, any> = {};
  const afterDiff: Record<string, any> = {};

  for (const key of Object.keys(after)) {
    if (before[key] !== after[key]) {
      beforeDiff[key] = before[key];
      afterDiff[key] = after[key];
    }
  }

  if (Object.keys(beforeDiff).length === 0) {
    return undefined;
  }

  return { before: beforeDiff, after: afterDiff };
}

/**
 * Action descriptions for common operations
 */
export const ACTION_DESCRIPTIONS: Record<AdminActionType, (target?: string) => string> = {
  CREATE_ORG: (name) => `Created organization: ${name}`,
  UPDATE_ORG: (name) => `Updated organization: ${name}`,
  DELETE_ORG: (name) => `Deleted organization: ${name}`,
  SUSPEND_ORG: (name) => `Suspended organization: ${name}`,
  ACTIVATE_ORG: (name) => `Activated organization: ${name}`,
  INVITE_USER: (email) => `Invited user: ${email}`,
  DISABLE_USER: (email) => `Disabled user: ${email}`,
  ENABLE_USER: (email) => `Enabled user: ${email}`,
  CHANGE_USER_ROLE: (email) => `Changed role for user: ${email}`,
  RESEND_INVITE: (email) => `Resent invite to: ${email}`,
  RESET_PASSWORD: (email) => `Reset password for: ${email}`,
  SET_PLAN: (orgName) => `Updated plan for: ${orgName}`,
  SET_SCOPE: (orgName) => `Updated scope config for: ${orgName}`,
  IMPERSONATE_ORG: (orgName) => `Entered support mode for: ${orgName}`,
  EXIT_IMPERSONATION: (orgName) => `Exited support mode for: ${orgName}`,
  CREATE_JOB_AS_ORG: (jobTitle) => `Created job in support mode: ${jobTitle}`,
  UPLOAD_DOC_AS_ORG: (docName) => `Uploaded document in support mode: ${docName}`,
  RUN_CHECK_AS_ORG: (jobTitle) => `Ran compliance check in support mode for: ${jobTitle}`,
  RERUN_CHECK: (runId) => `Re-ran compliance check: ${runId}`,
  DOWNLOAD_DEBUG_BUNDLE: (runId) => `Downloaded debug bundle for run: ${runId}`,
  CHANGE_ADMIN_ROLE: (email) => `Changed admin role for: ${email}`,
  ADD_ADMIN_USER: (email) => `Added admin user: ${email}`,
  REMOVE_ADMIN_USER: (email) => `Removed admin user: ${email}`,
};
